import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, orderBy, onSnapshot, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { FaFile } from 'react-icons/fa';


const UserRequestDetail = () => {
  const { requestId } = useParams();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const requestDoc = await getDoc(doc(db, 'requests', requestId));
        if (requestDoc.exists()) {
          setRequest({ id: requestDoc.id, ...requestDoc.data() });
          
          // Okundu olarak işaretle
          if (requestDoc.data().hasUnreadMessages && requestDoc.data().userId === user.uid) {
            await updateDoc(doc(db, 'requests', requestId), {
              hasUnreadMessages: false
            });
          }
        }
        setLoading(false);
      } catch (error) {
        console.error('Talep yükleme hatası:', error);
        setLoading(false);
      }
    };

    const messagesQuery = query(
      collection(db, 'requests', requestId, 'messages'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const newMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(newMessages);
    });

    fetchRequest();
    return () => unsubscribe();
  }, [requestId, user.uid]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    console.log('Seçilen dosyalar:', selectedFiles);
    setFiles(selectedFiles);
  };

  const uploadFiles = async () => {
    try {
      const uploadedFiles = [];
      setUploadProgress(0);
      
      for (const file of files) {
        console.log('Dosya yükleniyor:', file.name);
        
        const reader = new FileReader();
        const filePromise = new Promise((resolve, reject) => {
          reader.onload = async () => {
            try {
              console.log('Dosya okundu, base64 dönüşümü yapılıyor');
              const base64String = reader.result;
              
              resolve({
                name: file.name,
                base64: base64String,
                type: file.type,
                size: file.size,
                uploadedBy: user.uid,
                uploadedAt: new Date().toISOString()
              });
            } catch (error) {
              console.error('Dosya dönüştürme hatası:', error);
              reject(error);
            }
          };
          
          reader.onerror = (error) => {
            console.error('Dosya okuma hatası:', error);
            reject(error);
          };
        });

        reader.readAsDataURL(file);
        try {
          const uploadedFile = await filePromise;
          uploadedFiles.push(uploadedFile);
          setUploadProgress((prev) => prev + (100 / files.length));
        } catch (error) {
          console.error(`Dosya işleme hatası (${file.name}):`, error);
          throw error;
        }
      }
      
      console.log('Tüm dosyalar işlendi:', uploadedFiles);
      return uploadedFiles;
    } catch (error) {
      console.error('Toplu dosya işleme hatası:', error);
      setUploadError('Dosya işleme sırasında bir hata oluştu');
      throw error;
    }
  };

  const handleSendMessage = async (e) => {
    if (e) {
      e.preventDefault();
    }
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      
      let uploadedFiles = [];
      if (files.length > 0) {
        uploadedFiles = await uploadFiles();
      }

      const messageData = {
        content: newMessage,
        senderId: user.uid,
        senderName: user.displayName || user.email,
        senderEmail: user.email,
        createdAt: serverTimestamp(),
        files: uploadedFiles
      };

      const messagesRef = collection(db, 'requests', requestId, 'messages');
      const messageRef = await addDoc(messagesRef, messageData);

      // Admin ve editörlere bildirim gönder
      await addDoc(collection(db, 'notifications'), {
        userId: 'admin_editor',
        type: 'new_user_message',
        title: 'Yeni Kullanıcı Mesajı',
        content: `${user.displayName || user.email} yeni bir mesaj gönderdi`,
        requestId,
        messageId: messageRef.id,
        createdAt: serverTimestamp(),
        read: false,
        forAdminAndEditor: true
      });

      await updateDoc(doc(db, 'requests', requestId), {
        lastMessage: newMessage,
        lastMessageAt: serverTimestamp(),
        hasUnreadMessages: true
      });

      setNewMessage('');
      setFiles([]);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      scrollToBottom();
    } catch (error) {
      console.error('Mesaj gönderme hatası:', error);
      setUploadError('Mesaj gönderme sırasında bir hata oluştu');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="p-4">Yükleniyor...</div>;
  }

  if (!request) {
    return <div className="p-4">Talep bulunamadı.</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold mb-4">Talep Detayı</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <p className="text-gray-600 text-sm mb-1">Başlık</p>
              <p className="font-medium text-lg">{request.title}</p>
            </div>

            <div>
              <p className="text-gray-600 text-sm mb-1">Öncelik</p>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                request.priority === 'high' ? 'bg-red-100 text-red-800' :
                request.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-green-100 text-green-800'
              }`}>
                {request.priority === 'high' ? 'Yüksek' :
                 request.priority === 'medium' ? 'Orta' : 'Düşük'}
              </span>
            </div>

            <div>
              <p className="text-gray-600 text-sm mb-1">Kategori</p>
              <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                {request.category === 'general' ? 'Genel' :
                 request.category === 'excel' ? 'Excel' :
                 request.category === 'data-analysis' ? 'Veri Analizi' :
                 request.category === 'macro' ? 'Makro' :
                 request.category === 'automation' ? 'Otomasyon' :
                 request.category === 'reporting' ? 'Raporlama' :
                 request.category === 'formula' ? 'Formül' :
                 request.category === 'pivot' ? 'Pivot Tablo' :
                 request.category === 'chart' ? 'Grafik' :
                 request.category === 'vba' ? 'VBA' :
                 request.category === 'other' ? 'Diğer' :
                 request.category || 'Genel'}
              </span>
            </div>

            <div>
              <p className="text-gray-600 text-sm mb-1">Durum</p>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                request.status === 'completed' ? 'bg-green-100 text-green-800' :
                request.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {request.status === 'completed' ? 'Tamamlandı' :
                 request.status === 'in-progress' ? 'İşleniyor' : 'Beklemede'}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-gray-600 text-sm mb-1">Oluşturulma Tarihi</p>
              <p className="font-medium">
                {request.createdAt?.toDate().toLocaleString('tr-TR', {
                  dateStyle: 'full',
                  timeStyle: 'short'
                })}
              </p>
            </div>

            <div>
              <p className="text-gray-600 text-sm mb-1">Son Güncelleme</p>
              <p className="font-medium">
                {request.updatedAt?.toDate().toLocaleString('tr-TR', {
                  dateStyle: 'full',
                  timeStyle: 'short'
                }) || 'Güncelleme yok'}
              </p>
            </div>

            {request.completedAt && (
              <div>
                <p className="text-gray-600 text-sm mb-1">Tamamlanma Tarihi</p>
                <p className="font-medium">
                  {request.completedAt.toDate().toLocaleString('tr-TR', {
                    dateStyle: 'full',
                    timeStyle: 'short'
                  })}
                </p>
              </div>
            )}

            {request.assignedTo && (
              <div>
                <p className="text-gray-600 text-sm mb-1">İlgilenen Yetkili</p>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-medium">
                      {request.assignedTo.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{request.assignedTo.name}</p>
                    <p className="text-sm text-gray-500">{request.assignedTo.role === 'admin' ? 'Yönetici' : 'Editör'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="col-span-1 md:col-span-2">
            <p className="text-gray-600 text-sm mb-1">Talep İçeriği</p>
            <div className="bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">
              {request.content}
            </div>
          </div>

          {request.attachments && request.attachments.length > 0 && (
            <div className="col-span-1 md:col-span-2">
              <p className="text-gray-600 text-sm mb-2">Ekler</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {request.attachments.map((file, index) => (
                  <a
                    key={index}
                    href={file.url}
                    download={file.name}
                    className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                  >
                    <span className="text-blue-500 mr-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                      </svg>
                    </span>
                    <span className="text-sm font-medium text-gray-700 truncate">
                      {file.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold mb-6">Mesajlar</h3>
        <div className="space-y-4 mb-6 max-h-[500px] overflow-y-auto">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.senderId === user.uid ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] rounded-lg p-4 ${
                message.senderId === user.uid
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{message.senderName}</span>
                  <span className="text-sm opacity-75">
                    {message.createdAt?.toDate().toLocaleString()}
                  </span>
                </div>
                <div className="whitespace-pre-wrap">{message.content}</div>
                {message.files && message.files.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {message.files.map((file, index) => (
                      <a
                        key={index}
                        href={file.base64}
                        download={file.name}
                        className={`flex items-center space-x-2 text-sm hover:underline ${
                          message.senderId === user.uid ? 'text-white' : 'text-blue-500'
                        }`}
                      >
                        <FaFile />
                        <span>{file.name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {!request?.isLocked && request?.status !== 'completed' && (
          <form onSubmit={handleSendMessage} className="mt-4">
            <div className="space-y-4">
              <div className="relative">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (newMessage.trim()) {
                        handleSendMessage();
                      }
                    }
                  }}
                  disabled={sending}
                  placeholder="Mesajınızı yazın... (Göndermek için Enter, yeni satır için Shift + Enter)"
                  className="w-full p-3 border rounded-lg resize-none min-h-[120px] focus:outline-none focus:border-blue-500 focus:ring-0"
                  spellCheck="false"
                  autoComplete="off"
                />
                {sending && (
                  <div className="absolute right-3 top-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                <input
                  type="file"
                  onChange={handleFileChange}
                  multiple
                  className="file-input file-input-bordered flex-1"
                  disabled={sending}
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] transition-colors duration-200"
                >
                  {sending ? 'Gönderiliyor...' : 'Gönder'}
                </button>
              </div>

              {files.length > 0 && (
                <div className="text-sm text-gray-600">
                  {files.length} dosya seçildi
                </div>
              )}

              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              )}

              {uploadError && (
                <div className="text-red-500 text-sm">
                  {uploadError}
                </div>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default UserRequestDetail;